/* @flow */

import React, { useState } from 'react';
import numeral from 'numeral';
import round from 'lodash/round';
import styled from '@emotion/styled';
import colorUtils from 'color';
import { faStar, faLock, faTimes } from '@fortawesome/free-solid-svg-icons';
import NumericInput from 'react-numeric-input';

import Responsive from 'app/components/responsive/Responsive';
import { hover } from 'app/utils/cssUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { TableRow, TableCell } from './Table';
import PlayerBar from './PlayerBar';
import {
  getFactoredEntityPositionProjection,
  getFactoredEntityPositionSalary,
  getEntityPositionSalaryFactor
} from '../../../selectors/salaryExpenditureSelector';

type Props = {
  player: Object,
  style?: Object,
  showExposureWarning: boolean,
  sportHasTeam?: boolean,
  onRemove: (id: string, position: string) => void,
  onSelect: (entity: Object, positions: Array<Object>) => void,
  onSelectSettingsTab: (tab: string) => void,
  onToggleEntityProperty: (id: string, field: string) => void,
  onUpdateEntityProperty: (id: string, field: string, value: any) => void,
  isCaptain: boolean
};

const defaultProps = {
  sportHasTeam: true,
  style: undefined
};

export const FANTASY_INSIDER_STARRED_BG_COLOR = '#bfc6ce';

const OpenEntityRow = ({
  player,
  onRemove,
  onSelect,
  onToggleEntityProperty,
  onSelectSettingsTab,
  onUpdateEntityProperty,
  showExposureWarning,
  sportHasTeam,
  style,
  isCaptain
}: Props) => {
  const {
    awayAbbr,
    disabled,
    homeAbbr,
    id,
    idStr,
    isExcluded,
    isFavorite,
    isHome,
    isTeam,
    name,
    labels,
    teamAbbr,
    positionStr,
    teamImage,
    positions,
    salary,
    initials,
    selected,
    selectedPosition,
    insiderSalaryScoreRatio,
    insiderScoreUpdated,
    insiderForm,
    insiderWarning,
    insiderOpponent,
    insiderGames
  } = player;

  const canSelect = !disabled && !isExcluded;

  const [projection, setProjection] = useState(
    numeral(
      getFactoredEntityPositionProjection(
        insiderScoreUpdated,
        positions,
        isCaptain ? 'captain' : undefined
      )
    ).format('0.[0]')
  );
  const [updated, setUpdated] = useState(false);

  const toggleSelect = () => {
    if (selected) {
      onRemove(idStr, selectedPosition);
    } else if (canSelect) {
      onSelect({ id, idStr, isTeam }, positions, isCaptain);
    }
  };

  const handleToggleExclude = () => {
    if (!isExcluded && selected) {
      onRemove(idStr, selectedPosition);
    }
    onToggleEntityProperty(idStr, 'isExcluded');
  };

  const handleProjectionChange = val => {
    setProjection(val);
    setUpdated(true);
  };

  const handleUpdateEntityProperty = () => {
    if (!updated) {
      return;
    }

    const positionFactor = getEntityPositionSalaryFactor(
      positions,
      isCaptain ? 'captain' : undefined
    );

    const val = round(parseFloat(projection || '0') / positionFactor, 1);

    if (val !== insiderScoreUpdated) {
      onUpdateEntityProperty(idStr, 'insiderScoreUpdated', val);
    }
  };

  return (
    <StyledTableRow disabled={disabled} isFavorite={isFavorite} style={style}>
      <TableCell>
        <PlayerBar
          name={name}
          labels={labels}
          image={teamImage}
          initials={initials}
          warning={insiderWarning}
          onSelectSettingsTab={onSelectSettingsTab}
          exposureWarning={showExposureWarning}
        />
      </TableCell>
      <NarrowTableCell className="is-narrow">
        <NarrowCellContent width={sportHasTeam ? 55 : 75} largeScreenWidth={sportHasTeam ? 70 : 95}>
          {positionStr}
        </NarrowCellContent>
      </NarrowTableCell>
      {sportHasTeam && (
        <Responsive tabletLandscape desktopAndUp>
          <NarrowTableCell className="is-narrow">
            <NarrowCellContent width={40} largeScreenWidth={50}>
              {teamAbbr}
            </NarrowCellContent>
          </NarrowTableCell>
        </Responsive>
      )}
      <NarrowTableCell className="is-narrow">
        <NarrowCellContent width={sportHasTeam ? 50 : 70} largeScreenWidth={sportHasTeam ? 70 : 95}>
          {numeral(
            getFactoredEntityPositionSalary(salary, positions, isCaptain ? 'captain' : '')
          ).format('$0,0.[00]')}
        </NarrowCellContent>
      </NarrowTableCell>
      {sportHasTeam && (
        <Responsive tabletLandscape desktopAndUp>
          <NarrowTableCell className="is-narrow">
            <NarrowCellContent width={40} largeScreenWidth={50}>
              {insiderGames || '-'}
            </NarrowCellContent>
          </NarrowTableCell>
        </Responsive>
      )}
      {sportHasTeam && (
        <Responsive tabletLandscape desktopAndUp>
          <NarrowTableCell className="is-narrow">
            <NarrowCellContent width={40} largeScreenWidth={50}>
              {insiderOpponent || (isHome ? awayAbbr : homeAbbr)}
            </NarrowCellContent>
          </NarrowTableCell>
        </Responsive>
      )}
      <Responsive tabletLandscape desktopAndUp>
        <NarrowTableCell className="is-narrow">
          <NarrowCellContent
            width={sportHasTeam ? 40 : 80}
            largeScreenWidth={sportHasTeam ? 50 : 100}
          >
            {insiderSalaryScoreRatio || '-'}
          </NarrowCellContent>
        </NarrowTableCell>
      </Responsive>
      <Responsive tabletLandscape desktopAndUp>
        <NarrowTableCell className="is-narrow">
          <NarrowCellContent
            width={sportHasTeam ? 40 : 80}
            largeScreenWidth={sportHasTeam ? 50 : 100}
          >
            {insiderForm || '-'}
          </NarrowCellContent>
        </NarrowTableCell>
      </Responsive>
      <NarrowTableCell className="is-narrow">
        <NarrowCellContent width={150} largeScreenWidth={150}>
          <NumericInput
            key="id"
            name="projScore"
            min={-100}
            max={1000}
            step={0.1}
            size={5}
            onBlur={handleUpdateEntityProperty}
            onChange={handleProjectionChange}
            value={projection}
          />
          <IconContainer>
            <StarButton type="button" onClick={() => onToggleEntityProperty(idStr, 'isFavorite')}>
              <FontAwesomeIcon icon={faStar} color={isFavorite ? '#ffd85a' : 'gray'} />
            </StarButton>
            <LockButton type="button" onClick={toggleSelect}>
              <FontAwesomeIcon
                icon={faLock}
                color={selected ? 'black' : 'gray'}
                style={{ opacity: canSelect ? 1 : 0.25 }}
              />
            </LockButton>
            <ExcludeButton type="button" onClick={handleToggleExclude}>
              <FontAwesomeIcon icon={faTimes} color={isExcluded ? 'red' : 'gray'} />
            </ExcludeButton>
          </IconContainer>
        </NarrowCellContent>
      </NarrowTableCell>
    </StyledTableRow>
  );
};

OpenEntityRow.defaultProps = defaultProps;

export default React.memo(OpenEntityRow);

const StyledTableRow = styled(TableRow)(
  ({ theme }) => ({
    fontSize: theme.fontSize.xsmall,
    backgroundColor: theme.color.white
  }),
  ({ disabled, theme }) =>
    !disabled
      ? hover({
          backgroundColor: colorUtils(theme.color.white)
            .darken(0.05)
            .hex()
        })
      : {},
  ({ isFavorite }) =>
    isFavorite
      ? {
          backgroundColor: FANTASY_INSIDER_STARRED_BG_COLOR
        }
      : {}
);

const NarrowTableCell = styled(TableCell)({
  flexGrow: '0 !important'
});

const NarrowCellContent = styled.div(
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  ({ width }) => (width ? { width } : {}),
  ({ theme, largeScreenWidth }) =>
    largeScreenWidth ? { [theme.mediaQuery.desktopLargeOnly]: { width: largeScreenWidth } } : {}
);

const StarButton = styled.button({
  '& > *:hover': {
    color: '#ffd85a'
  }
});

const LockButton = styled.button({
  '& > *:hover': {
    color: 'black'
  }
});

const ExcludeButton = styled.button(({ theme }) => ({
  '& > *:hover': {
    color: theme.color.danger
  }
}));

const IconContainer = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  '& > *': {
    padding: '0 0.25rem'
  }
});
